/**
 * The administrator session, as the app sees it.
 *
 * ── WHAT THIS FILE IS ────────────────────────────────────────────────────
 * The client side of the Worker's four session routes (routes/session.ts):
 *
 *   POST /api/enterprise/auth/start    { email }         204, always
 *   POST /api/enterprise/auth/verify   { email, code }   200 + the cookie
 *   GET  /api/enterprise/session                         200, 401 or 503
 *   POST /api/enterprise/auth/logout                     204 + a cleared cookie
 *
 * Every call here returns a plain object and never throws, because each of
 * the four has a designed failure that the screen must render rather than
 * crash on, and the difference between them is the whole screen:
 *
 *   401  signed out           the visitor goes to /login
 *   503  AUTH_UNCONFIGURED    the deployment cannot check a cookie at all
 *                             (no SESSION_PEPPER). Not a sign-in problem,
 *                             and sending someone round the sign-in loop
 *                             for it would never end.
 *   network                   P3 could not be reached. Try again.
 *
 * ── WHAT IT DOES NOT DO ──────────────────────────────────────────────────
 * It holds no token. The session is an HttpOnly cookie the browser carries;
 * the app never reads it and could not. This file caches nothing: every
 * signed-in screen asks the Worker once on mount. Since Sep 11 2026 the
 * screen draws the answer this tab already had while that call is in flight
 * (`lib/live-cache.ts`), so moving between Settings, Billing and Team no
 * longer flashes the splash, and a session ended elsewhere (a role change, a
 * removal, a logout in another tab) is seen a moment later rather than only
 * on the next screen.
 */

import type { SessionPayload } from "~/worker/lib/api-types";
import { NETWORK_ERROR, errorOf, request, type ApiError, type ApiResult } from "~/app/lib/api";

export type { ApiError, ApiResult, SessionPayload };
export { NETWORK_ERROR, errorOf };

export type ViewerRole = SessionPayload["role"];

export type SessionState =
  | { readonly kind: "loading" }
  | { readonly kind: "signed-out" }
  /** The Worker cannot check a session on this deployment. */
  | { readonly kind: "unconfigured"; readonly message: string }
  /** P3 could not be reached, or answered with something unexpected. */
  | { readonly kind: "down"; readonly message: string }
  | { readonly kind: "signed-in"; readonly session: SessionPayload };

/** What `auth/verify` answers with beside the cookie. */
export interface VerifiedPayload {
  readonly orgName: string;
  readonly role: ViewerRole;
  readonly memberships: readonly { readonly membershipId: string; readonly orgName: string }[];
}

/**
 * Who is signed in, if anyone. Never throws: the four outcomes are the four
 * screens the caller has to render.
 */
export async function fetchSession(): Promise<SessionState> {
  const result = await request("/api/enterprise/session");
  if (result === null) return { kind: "down", message: NETWORK_ERROR.message };
  if (result.status === 200 && isSessionPayload(result.json)) {
    return { kind: "signed-in", session: result.json };
  }
  if (result.status === 401) return { kind: "signed-out" };
  const error = errorOf(result.json);
  if (result.status === 503 && error.code === "AUTH_UNCONFIGURED") {
    return { kind: "unconfigured", message: error.message };
  }
  return { kind: "down", message: error.message };
}

function isSessionPayload(json: unknown): json is SessionPayload {
  if (!json || typeof json !== "object") return false;
  const o = json as Record<string, unknown>;
  return (
    typeof o["orgName"] === "string" &&
    typeof o["email"] === "string" &&
    typeof o["memberId"] === "string" &&
    (o["role"] === "owner" || o["role"] === "admin" || o["role"] === "viewer")
  );
}

/**
 * Ask for a code. The Worker answers 204 for every address, known or not,
 * so a 204 here means "if that address is on a team, a code is on its way"
 * and never "that address exists".
 */
export async function startLogin(email: string): Promise<ApiResult> {
  const result = await request("/api/enterprise/auth/start", { method: "POST", body: { email } });
  if (result === null) return { ok: false, status: null, error: NETWORK_ERROR };
  if (result.status === 204) return { ok: true, value: undefined };
  return { ok: false, status: result.status, error: errorOf(result.json) };
}

/** Exchange the code for the session cookie. The Worker sets the cookie. */
export async function verifyLogin(email: string, code: string): Promise<ApiResult<VerifiedPayload>> {
  const result = await request("/api/enterprise/auth/verify", {
    method: "POST",
    body: { email, code },
  });
  if (result === null) return { ok: false, status: null, error: NETWORK_ERROR };
  if (result.status === 200 && isVerifiedPayload(result.json)) {
    return { ok: true, value: result.json };
  }
  return { ok: false, status: result.status, error: errorOf(result.json) };
}

function isVerifiedPayload(json: unknown): json is VerifiedPayload {
  if (!json || typeof json !== "object") return false;
  const o = json as Record<string, unknown>;
  return typeof o["orgName"] === "string" && typeof o["role"] === "string";
}

/**
 * End the session. The Worker revokes the row and clears the cookie; a
 * failure here (the session was already gone, P3 unreachable) still leaves
 * the caller free to navigate away, which is why this resolves either way.
 */
export async function logout(): Promise<void> {
  await request("/api/enterprise/auth/logout", { method: "POST" });
}

/* ==========================================================================
   Presentation helpers for the signed-in chrome
   ========================================================================== */

/** What the rail footer needs about the signed-in person. */
export interface Viewer {
  readonly name: string | null;
  readonly email: string;
  readonly role: ViewerRole;
  /** The current membership, and every institution this person administers
   *  (Sep 10 2026). The rail offers a switch when there is more than one. */
  readonly membershipId?: string;
  readonly memberships?: readonly { readonly membershipId: string; readonly orgName: string }[];
}

export function viewerOf(session: SessionPayload | undefined): Viewer | undefined {
  if (!session) return undefined;
  return {
    name: session.memberName,
    email: session.email,
    role: session.role,
    membershipId: session.memberId,
    memberships: session.memberships,
  };
}

/** "owner" to "Owner", for the rail footer's "Owner access". */
export function roleLabel(role: ViewerRole): "Owner" | "Admin" | "Viewer" {
  return role === "owner" ? "Owner" : role === "admin" ? "Admin" : "Viewer";
}

/**
 * Two letters for the avatar: the initials of a name, or the first two
 * letters of the address when the membership has no name yet (the
 * provisioning script writes one; an invitation may not).
 */
export function initialsOf(name: string | null, email: string): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0]!.charAt(0)}${words[words.length - 1]!.charAt(0)}`.toUpperCase();
  }
  if (words.length === 1 && words[0]!.length >= 2) return words[0]!.slice(0, 2).toUpperCase();
  return email.slice(0, 2).toUpperCase();
}
